import type { Registry as Services } from '@ember/service';
import type MarketPerformanceDataItem from 'lumen/models/market-performance/data-item';
import type { MarketPerformanceMeta } from 'lumen/models/market-performance/data-item';
import type MarketPerformanceDataStatus from 'lumen/models/market-performance/data-status';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  GROUP_BY,
  type GroupBy,
} from 'lumen/utils/constants/market-snapshots-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

/** URL query params shared by every snapshot view (validated in the transition service). */
export type MarketSnapshotsParams = {
  fromDate?: string;
  groupBy?: GroupBy;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  toDate?: string;
};

export interface SnapshotItemsServices {
  alerting: Services['alerting'];
  errorTracking: Services['error-tracking'];
  store: Services['store'];
}

export type SnapshotDateRange = { from: string; to: string };

export type SnapshotPagination = { page: number; pageSize: number };

/** Month rows are few and API-ordered: no sort, no pagination. */
export type SnapshotRows =
  | { groupBy: typeof GROUP_BY.MONTH }
  | {
      groupBy: typeof GROUP_BY.PROPERTY;
      orderBy: string;
      pagination: SnapshotPagination;
    };

export interface SnapshotItemsParams {
  compare: string;
  dateRange: SnapshotDateRange;
  marketId: string;
  /** Pickup periods per metric, e.g. `{ otb: '1,7' }` → `pickup[otb]=1,7`. */
  pickup?: Record<string, string>;
  rows: SnapshotRows;
  /** Adds the transient / group breakdown to every row and total. */
  segments?: boolean;
  view: string;
}

export interface SnapshotItems {
  items: MarketPerformanceDataItem[];
  meta: MarketPerformanceMeta;
  // The params this result was loaded with, so consumers can tell whether it
  // still matches the current query params.
  params: SnapshotItemsParams;
}

/** Loads one page of a market snapshot view; errors alert, are tracked and rethrown silently. */
export async function fetchSnapshotItems(
  { alerting, errorTracking, store }: SnapshotItemsServices,
  params: SnapshotItemsParams,
): Promise<SnapshotItems> {
  const { compare, dateRange, marketId, pickup, rows, segments, view } = params;

  try {
    const response = await store.query<MarketPerformanceDataItem>(
      'market-performance/data-item',
      {
        namespace: APIGATEWAY,
        typeKey: `reservations/market/${marketId}/performance`,
        view,
        compare,
        group_by: rows.groupBy,
        start_date: dateRange.from,
        end_date: dateRange.to,
        market_id: marketId,
        ...(pickup && { pickup }),
        ...(segments && { segments }),
        // Month rows are few and API-ordered: no sort, no pagination.
        ...(rows.groupBy === GROUP_BY.PROPERTY && {
          order_by: rows.orderBy,
          page: rows.pagination.page,
          page_size: rows.pagination.pageSize,
        }),
      },
    );

    return {
      items: response,
      meta: response.meta as unknown as MarketPerformanceMeta,
      params,
    };
  } catch (error) {
    void alerting.showErrorAlert();
    errorTracking.captureException(error);

    throw new SilentTaskError(getErrorMessage(error));
  }
}

/** Loads the market's per-subscription data statuses; errors are only tracked. */
export async function fetchDataStatuses(
  { errorTracking, store }: Omit<SnapshotItemsServices, 'alerting'>,
  marketId: string,
): Promise<MarketPerformanceDataStatus[]> {
  try {
    return await store.query<MarketPerformanceDataStatus>(
      'market-performance/data-status',
      { market_id: marketId },
    );
  } catch (error) {
    errorTracking.captureException(error);

    return [];
  }
}
